import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquare, Clock, Send } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.[0]?.msg || "Could not send message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us — Subscribe or Ask a Question"
        description="Contact Asian Picks to subscribe to our Asian Handicap tip packages or ask a question. We reply within 24 hours, seven days a week. asianhpicks@gmail.com"
        path="/contact"
        keywords="contact Asian Picks, subscribe AH tips, football tips support, Asian Handicap subscription contact"
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            Contact
          </div>
          <h1
            data-testid="contact-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            Questions? <span className="gold-text">Ready to subscribe?</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
            Send us a message using the form below. We reply to every real
            enquiry within 24 hours, seven days a week.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="p-6 rounded-2xl bg-white border border-ink-200">
                <div className="w-10 h-10 rounded-lg bg-emerald-700/10 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-emerald-700" />
                </div>
                <h3 className="font-display font-semibold text-lg text-ink-900 mb-1">
                  Email us directly
                </h3>
                <a
                  href="mailto:asianhpicks@gmail.com"
                  data-testid="contact-email-link"
                  className="text-emerald-700 hover:text-gold-600 font-mono text-sm"
                >
                  asianhpicks@gmail.com
                </a>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-ink-200">
                <div className="w-10 h-10 rounded-lg bg-emerald-700/10 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-emerald-700" />
                </div>
                <h3 className="font-display font-semibold text-lg text-ink-900 mb-1">
                  Response time
                </h3>
                <p className="text-ink-500 text-sm leading-relaxed">
                  Within 24 hours, seven days a week. Subscribe enquiries are
                  usually replied to in under 4 hours during European daytime.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-ink-200">
                <div className="w-10 h-10 rounded-lg bg-emerald-700/10 flex items-center justify-center mb-4">
                  <MessageSquare className="w-5 h-5 text-emerald-700" />
                </div>
                <h3 className="font-display font-semibold text-lg text-ink-900 mb-1">
                  Before you write
                </h3>
                <p className="text-ink-500 text-sm leading-relaxed">
                  Most questions are already answered on our{" "}
                  <a href="/faq" className="text-emerald-700 underline">
                    FAQ page
                  </a>
                  . Have a look — it's a quick read.
                </p>
              </div>
            </div>

            {/* Form */}
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="lg:col-span-3 p-8 sm:p-10 rounded-2xl bg-white border border-ink-200 shadow-lg shadow-ink-200/30 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-ink-900 mb-2">
                    Your name
                  </label>
                  <input
                    id="name"
                    data-testid="contact-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="w-full px-4 py-3 rounded-lg border border-ink-200 bg-ink-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-ink-900 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="w-full px-4 py-3 rounded-lg border border-ink-200 bg-ink-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent transition-all"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-ink-900 mb-2">
                  Subject
                </label>
                <input
                  id="subject"
                  data-testid="contact-subject"
                  type="text"
                  required
                  value={form.subject}
                  onChange={update("subject")}
                  className="w-full px-4 py-3 rounded-lg border border-ink-200 bg-ink-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent transition-all"
                  placeholder="Subscription enquiry"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-ink-900 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  data-testid="contact-message"
                  rows={7}
                  required
                  value={form.message}
                  onChange={update("message")}
                  className="w-full px-4 py-3 rounded-lg border border-ink-200 bg-ink-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent transition-all resize-none"
                  placeholder="Which package are you interested in? Any questions before subscribing?"
                />
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-semibold text-sm transition-all shadow-lg shadow-emerald-700/20"
              >
                {submitting ? "Sending…" : "Send message"}
                <Send className="w-4 h-4" />
              </button>

              <p className="text-xs text-ink-500 leading-relaxed">
                By submitting this form you consent to us contacting you at the
                email address provided. We never share your details with third
                parties and do not use them for marketing.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
